import { Link, Outlet, useLocation, useMatches } from "@tanstack/react-router";
import { Home } from "lucide-react";
import { Fragment, useEffect, type CSSProperties } from "react";

import { crumbTrail } from "./crumbs";
import { LayoutActionsSlot } from "./layout-slots";
import { LayoutPanelSlot } from "./panel-slot";
import { AppSidebar, SIDEBAR_WIDTH, SIDEBAR_WIDTH_ICON } from "./sidebar/app-sidebar";
import { AnimatedThemeToggler } from "@/components/ui/animated-theme-toggler";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SidebarInset, SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { navbarActions } from "@/constants/navbar-actions";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

// The wash behind the frosted sidebar: two soft radial glows in the top-left
// corner and halfway down the left edge, fading into the page tint. Both pull
// from the theme's decorative tokens, so a retheme recolors the glow with it.
// The sizes are fixed pixels on purpose: the glow belongs to the sidebar, and a
// viewport-relative size would smear it under the content panel on wide screens.
const backdropStyle = {
  background: [
    "radial-gradient(520px 420px at 0% 0%, color-mix(in oklch, var(--color-4) 26%, transparent), transparent 70%)",
    "radial-gradient(420px 380px at 10% 55%, color-mix(in oklch, var(--color-2) 14%, transparent), transparent 70%)",
    "var(--page-background)",
  ].join(", "),
} as CSSProperties;

// The glass sidebar is wider than the default. Set the width on the provider so
// the layout gap and the panel stay in sync (see app-sidebar for why). The icon
// width is bumped too so the collapsed rail isn't cramped.
const sidebarStyle = { "--sidebar-width": SIDEBAR_WIDTH, "--sidebar-width-icon": SIDEBAR_WIDTH_ICON } as CSSProperties;

/**
 * The toggle that lives in front of the breadcrumbs. It owns the sidebar in the
 * two states where the sidebar can't show a toggle of its own: the collapsed
 * rail (only wide enough for the logo) and mobile (an off-canvas sheet, so its
 * in-sheet toggle is hidden with it).
 *
 * It grows its slot open rather than fading on top of the breadcrumbs, so the
 * trail slides right to make room while the sidebar shrinks. Width, margin and
 * opacity all run the panel's 200ms `linear`, so the whole shell reads as one
 * motion. The slot is the 28px button plus the gap to the trail, which is why the
 * header itself no longer sets a `gap`.
 */
const HeaderSidebarTrigger = () => {
  const { state, isMobile } = useSidebar();
  const visible = isMobile || state === "collapsed";

  return (
    <div
      className={cn(
        "flex shrink-0 items-center overflow-hidden transition-[width,margin,opacity] duration-200 ease-linear",
        visible ? "-ml-1 w-12 opacity-100" : "w-0 opacity-0",
      )}
    >
      <SidebarTrigger
        tabIndex={visible ? undefined : -1}
        aria-hidden={!visible}
        className="text-muted-foreground hover:bg-foreground/10 hover:text-foreground dark:hover:bg-foreground/10 max-md:size-10 max-md:[&>svg]:size-5!"
      />
    </div>
  );
};

export const PageLayout = () => {
  const { pathname } = useLocation();
  const matches = useMatches();
  // Reset scroll on navigation without remounting the subtree. Remounting (via a
  // `key`) would replay every page's mount animation on each route change. The
  // window is what scrolls: the page column grows with its content.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);

  const crumbs = crumbTrail(matches);

  return (
    <SidebarProvider style={sidebarStyle}>
      {/* The backdrop lives behind everything and spans the whole viewport, so the
        glow reads through the transparent sidebar while the solid content panel
        sits on top of it. Pinned fixed so it stays put as the sidebar collapses
        and the content scrolls. */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10" style={backdropStyle} />
      <AppSidebar />
      {/* min-w-0 lets the inset shrink beside the sidebar, so a wide table scrolls instead of pushing a docked panel off screen. */}
      <SidebarInset className="relative z-10 min-w-0 md:my-2 md:mr-2 md:overflow-clip md:rounded-xl md:border md:border-border/60 bg-solid-background md:shadow-lg">
        <header className="flex pt-2.5 shrink-0 items-center px-4 md:px-6 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
          <HeaderSidebarTrigger />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem className="hidden md:block">
                <BreadcrumbLink asChild className="flex items-center gap-1">
                  <Link to="/dashboard">
                    <Home className="size-3.5" />
                    {m.layout_breadcrumb_home()}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {crumbs.map((crumb, index) => {
                const isLast = index === crumbs.length - 1;

                return (
                  <Fragment key={crumb.to}>
                    <BreadcrumbSeparator className="hidden md:block" />
                    <BreadcrumbItem>
                      {isLast ? (
                        <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <Link to={crumb.to}>{crumb.label}</Link>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </Fragment>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
          <div className="ml-auto flex items-center gap-2 text-muted-foreground">
            <LayoutActionsSlot />
            {navbarActions.map((action) => (
              <action.component key={action.id} />
            ))}
            <AnimatedThemeToggler />
          </div>
        </header>
        <div className="flex min-h-0 flex-1">
          <div
            // Clip, not hidden or auto: neither ever scrolled (the window does), but both made
            // this column the box `position: sticky` sticks in, so docked bars never stuck.
            className="flex min-w-0 flex-1 flex-col gap-4 overflow-x-clip px-4 pt-2 pb-4 md:px-6"
          >
            <Outlet />
          </div>
          <LayoutPanelSlot />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};
